// Kredensial OSS tidak lagi ditanam di bundle lewat env. Kredensial diambil
// dari backend dalam bentuk terenkripsi, lalu didekripsi di browser dengan
// AES-256-GCM memakai VUE_APP_OSS_PAYLOAD_KEY.
//
// Endpoint kredensial mewajibkan token, jadi modul ini hanya boleh dipakai
// setelah user login. Seluruh pemakaian OSS di webadmin berada di belakang
// guard login pada App.vue, jadi tidak ada pencegahan tambahan di sini.
//
// Ada dua jalur pemakai:
//   - getOssClient()          -> client ali-oss (dipakai lewat Vue.prototype.$alioss)
//   - applyOssCredentials(AWS) -> mengisi AWS.config untuk jalur upload aws-sdk

import axios from "axios";

const CREDENTIAL_PATH = "/schoolcred/file/thirdparty/static/student";
const REQUEST_TIMEOUT = 15000;

// Kredensial di-cache supaya satu halaman dengan banyak upload tidak memukul
// endpoint berkali-kali, tetapi tetap dibuang sebelum stsToken (bila ada)
// kedaluwarsa. Client ali-oss punya mekanisme refresh sendiri di bawah.
const CREDENTIAL_TTL = 1500000;

// Endpoint membalas 4xx/5xx (bukan 401) ketika token bermasalah. Tanpa jeda,
// setiap pemanggil yang gagal langsung memicu request baru. Jeda ini menahan
// badai tersebut.
const RETRY_COOLDOWN = 5000;

let clientPromise = null;
let payloadKeyPromise = null;
let credentialPromise = null;
let cachedCredential = null;
let cachedCredentialAt = 0;
let lastFailureAt = 0;

function b64ToBytes(b64) {
    return Uint8Array.from(atob(b64), function (c) {
        return c.charCodeAt(0);
    });
}

// API_BASE_URL tidak konsisten soal trailing slash antar file config
// (prod.env.js_for_dev tidak memakainya), sedangkan CREDENTIAL_PATH selalu
// diawali slash.
function getApiBaseUrl() {
    return String(process.env.API_BASE_URL || "").replace(/\/+$/, "");
}

// Token disimpan lewat vue-ls, jadi bentuknya {"value": ..., "expire": ...}.
// Nilainya sudah mengandung prefix "Bearer " dan dipakai apa adanya, sama
// seperti getConfig() di modules/auth/services/auth.js.
function getAuthorization() {
    var raw = null;

    try {
        raw = localStorage.getItem("token");
    } catch (error) {
        raw = null;
    }

    if (!raw) return null;

    try {
        return JSON.parse(raw).value || null;
    } catch (error) {
        return null;
    }
}

// crypto.subtle hanya tersedia di secure context. https dan localhost aman,
// tetapi devServer juga melayani IP LAN lewat http -- di sana nilainya
// undefined dan kegagalannya muncul sebagai TypeError yang membingungkan.
function getSubtle() {
    var subtle = window.crypto && window.crypto.subtle;

    if (!subtle) {
        throw new Error(
            "Kredensial OSS memerlukan secure context. Gunakan https atau localhost, bukan IP LAN lewat http."
        );
    }

    return subtle;
}

function getPayloadKey() {
    if (!payloadKeyPromise) {
        var rawKey = process.env.VUE_APP_OSS_PAYLOAD_KEY;

        if (!rawKey) {
            return Promise.reject(
                new Error(
                    "VUE_APP_OSS_PAYLOAD_KEY belum diset pada environment ini"
                )
            );
        }

        payloadKeyPromise = getSubtle()
            .importKey("raw", b64ToBytes(rawKey), "AES-GCM", false, ["decrypt"])
            .catch(function (error) {
                payloadKeyPromise = null;
                throw error;
            });
    }

    return payloadKeyPromise;
}

async function decryptPayload(payload) {
    var key = await getPayloadKey();

    // Backend mengirim ciphertext dan auth tag sebagai field terpisah,
    // sedangkan WebCrypto menuntut keduanya menyatu dengan tag di belakang
    // ciphertext.
    var cipherBytes = b64ToBytes(payload.data);
    var tagBytes = b64ToBytes(payload.tag);
    var combined = new Uint8Array(cipherBytes.length + tagBytes.length);
    combined.set(cipherBytes);
    combined.set(tagBytes, cipherBytes.length);

    var plain = await getSubtle().decrypt(
        { name: "AES-GCM", iv: b64ToBytes(payload.iv) },
        key,
        combined
    );

    return JSON.parse(new TextDecoder().decode(plain));
}

async function fetchOssCredential() {
    var authorization = getAuthorization();

    if (!authorization) {
        throw new Error("Kredensial OSS hanya bisa diambil setelah login");
    }

    var payload;

    try {
        var response = await axios.get(getApiBaseUrl() + CREDENTIAL_PATH, {
            timeout: REQUEST_TIMEOUT,
            headers: { Authorization: authorization },
        });

        payload = response.data;
    } catch (error) {
        var description =
            error.response && error.response.data
                ? error.response.data.description
                : null;

        throw new Error(description || "Gagal mengambil kredensial OSS");
    }

    return decryptPayload(payload);
}

// Kredensial yang sudah dicache dan masih segar dipakai ulang; kegagalan
// menyalakan cooldown supaya pemanggil berikutnya tidak langsung menembak
// endpoint lagi.
function getCredential() {
    if (
        cachedCredential &&
        Date.now() - cachedCredentialAt < CREDENTIAL_TTL
    ) {
        return Promise.resolve(cachedCredential);
    }

    if (!credentialPromise) {
        if (lastFailureAt && Date.now() - lastFailureAt < RETRY_COOLDOWN) {
            return Promise.reject(
                new Error(
                    "Kredensial OSS baru saja gagal diambil, menunggu sebelum mencoba lagi"
                )
            );
        }

        credentialPromise = fetchOssCredential()
            .then(function (cred) {
                cachedCredential = cred;
                cachedCredentialAt = Date.now();
                credentialPromise = null;
                lastFailureAt = 0;

                return cred;
            })
            .catch(function (error) {
                credentialPromise = null;
                lastFailureAt = Date.now();
                throw error;
            });
    }

    return credentialPromise;
}

export default function getOssClient() {
    if (!clientPromise) {
        clientPromise = getCredential()
            .then(function (cred) {
                // ali-oss adalah modul CJS; dipakai lewat require seperti
                // sebelum refactor ini supaya bundling tidak berubah.
                var OSS = require("ali-oss");

                var options = {
                    region: process.env.VUE_APP_OSS_REGION,
                    accessKeyId: cred.accessKeyId,
                    accessKeySecret: cred.accessKeySecret,
                    bucket: process.env.VUE_APP_OSS_BUCKET,
                    // `secure` sengaja tidak diset: default ali-oss mengikuti
                    // protokol halaman, sama persis dengan perilaku sebelumnya.
                };

                if (cred.stsToken) {
                    options.stsToken = cred.stsToken;
                    options.refreshSTSToken = fetchOssCredential;
                    options.refreshSTSTokenInterval = 1800000;
                }

                return new OSS(options);
            })
            .catch(function (error) {
                clientPromise = null;
                throw error;
            });
    }

    return clientPromise;
}

// Jalur upload lama memakai aws-sdk (AWS.S3 yang diarahkan ke endpoint OSS).
// Helper ini menggantikan penyetelan AWS.config.* yang dulu membaca env.
export async function applyOssCredentials(AWS) {
    var cred = await getCredential();

    AWS.config.accessKeyId = cred.accessKeyId;
    AWS.config.secretAccessKey = cred.accessKeySecret;

    // AWS.config bersifat global, jadi sessionToken selalu ditimpa supaya sisa
    // token sesi sebelumnya tidak ikut terbawa.
    AWS.config.sessionToken = cred.stsToken || null;

    return cred;
}

// Kredensial terikat pada identitas pemanggil. Saat logout klien lama dibuang
// agar sesi berikutnya tidak memakai sisa kredensial sesi sebelumnya.
export function resetOssClient() {
    clientPromise = null;
    payloadKeyPromise = null;
    credentialPromise = null;
    cachedCredential = null;
    cachedCredentialAt = 0;
    lastFailureAt = 0;
}



// WEBPACK FOOTER //
// ./src/modules/global/services/oss.js